import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { cn, formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Plus, Search, Phone, Mail, Building2, Calendar, 
  DollarSign, User, Trash2, UserPlus, GripVertical, 
  ChevronRight, X, Clock, MessageSquare, Send, AlertTriangle,
  TrendingUp, Eye, History, CheckCircle2, ArrowRight, Sparkles,
  ListTodo, Shield, Activity, HelpCircle, CheckSquare
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Funnel, FunnelChart, LabelList
} from 'recharts';
import { AILeadScoreBadge } from '@/components/ai/AILeadScore';
import AILeadScore from '@/components/ai/AILeadScore';
import { ActivityTimeline, TasksList, LeadCompliancePanel } from '@/components/leads';
import LeadActivityTimeline from '@/components/leads/LeadActivityTimeline';
import AdviserPicker from '@/components/shared/AdviserPicker';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Wrapper component for AI Lead Score in modal
function AILeadScoreComponent({ lead }) {
  return <AILeadScore lead={lead} />;
}

const STAGE_COLORS = {
  new: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700', dot: 'bg-gray-500', hex: '#6B7280' },
  qualified: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', dot: 'bg-blue-500', hex: '#3B82F6' },
  proposal: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700', dot: 'bg-purple-500', hex: '#8B5CF6' },
  negotiating: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-500', hex: '#F59E0B' },
  won: { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-700', dot: 'bg-green-500', hex: '#10B981' },
  lost: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', dot: 'bg-red-500', hex: '#EF4444' }
};

const LEAD_SOURCES = [
  'Referral', 'Website', 'LinkedIn', 'Event/Conference', 
  'Cold Outreach', 'Existing Client', 'Partner', 'Other'
];

// Calculate days in current stage
function getDaysInStage(lead) {
  const stageDate = lead.stageChangedAt || lead.createdAt;
  if (!stageDate) return 0;
  const days = Math.floor((new Date() - new Date(stageDate)) / (1000 * 60 * 60 * 24));
  return days;
}

// Get aging indicator color
function getAgingColor(days, stage) {
  if (['won', 'lost'].includes(stage)) return null;
  if (days <= 3) return 'bg-green-500';
  if (days <= 7) return 'bg-yellow-500';
  if (days <= 14) return 'bg-orange-500';
  return 'bg-red-500';
}

// Get aging label
function getAgingLabel(days) {
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

// Lead Card Component with Aging
function LeadCard({ lead, onDragStart, onDragEnd, onClick, isDragging, selected, onSelect, showSelect }) {
  const daysInStage = getDaysInStage(lead);
  const agingColor = getAgingColor(daysInStage, lead.stage);
  
  return (
    <div
      draggable={!showSelect}
      onDragStart={(e) => !showSelect && onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={() => showSelect ? onSelect?.(lead.id) : onClick(lead)}
      className={cn(
        "bg-white rounded-lg border p-3 cursor-pointer relative",
        "hover:shadow-md transition-all",
        isDragging && "opacity-50 rotate-2",
        selected && "ring-2 ring-[#A86A3A] border-[#A86A3A]",
        daysInStage > 14 && !['won', 'lost'].includes(lead.stage) 
          ? "border-red-300 bg-red-50/30" 
          : "border-[#E6E0D9] hover:border-[#4A3B32]/30"
      )}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* Aging Indicator */}
      {agingColor && (
        <div className="absolute top-0 left-0 right-0 h-1 rounded-t-lg overflow-hidden">
          <div className={cn("h-full", agingColor)} />
        </div>
      )}
      
      <div className="flex items-start justify-between mb-2 mt-1">
        {showSelect && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onSelect?.(lead.id)}
            onClick={e => e.stopPropagation()}
            className="mt-0.5 mr-2 rounded border-stone-300 flex-shrink-0"
          />
        )}
        <h4 className="font-medium text-[#2C2420] text-sm truncate flex-1">{lead.name}</h4>
        {!showSelect && <GripVertical className="w-4 h-4 text-[#8C8078] opacity-50 cursor-grab" />}
      </div>
      
      {lead.company && (
        <p className="text-xs text-[#5D524D] flex items-center gap-1 mb-1">
          <Building2 className="w-3 h-3" />
          {lead.company}
        </p>
      )}
      
      {lead.contactName && (
        <p className="text-xs text-[#5D524D] flex items-center gap-1 mb-1">
          <User className="w-3 h-3" />
          {lead.contactName}
        </p>
      )}
      
      {lead.estimatedValue && (
        <p className="text-xs font-medium text-[#4A3B32] flex items-center gap-1 mb-2">
          <DollarSign className="w-3 h-3" />
          {formatCurrency(lead.estimatedValue)}
        </p>
      )}
      
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#E6E0D9]">
        <span className="text-xs text-[#8C8078]">{lead.source}</span>
        <div className="flex items-center gap-2">
          {/* AI Score Badge */}
          {lead.aiScore && (
            <AILeadScoreBadge score={lead.aiScore} confidence={lead.aiScoreConfidence} />
          )}
          {/* Notes indicator */}
          {lead.notesCount > 0 && (
            <span className="flex items-center gap-0.5 text-xs text-[#8C8078]">
              <MessageSquare className="w-3 h-3" />
              {lead.notesCount}
            </span>
          )}
          {/* Aging badge */}
          {agingColor && (
            <span className={cn(
              "text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1",
              daysInStage > 7 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
            )}>
              <Clock className="w-3 h-3" />
              {getAgingLabel(daysInStage)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Stage Column Component
function StageColumn({ stage, leads, onDragOver, onDrop, onDragLeave, isDragOver, onLeadClick, onDragStart, onDragEnd, draggedLead, bulkMode, selectedLeads, onToggleSelect }) {
  const colors = STAGE_COLORS[stage.id] || STAGE_COLORS.new;
  const stageLeads = leads.filter(l => l.stage === stage.id);
  const totalValue = stageLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
  const avgDays = stageLeads.length > 0 
    ? Math.round(stageLeads.reduce((sum, l) => sum + getDaysInStage(l), 0) / stageLeads.length)
    : 0;
  
  return (
    <div
      className={cn(
        "flex-shrink-0 w-72 rounded-xl p-3 transition-all",
        colors.bg,
        isDragOver && "ring-2 ring-[#4A3B32] ring-opacity-50"
      )}
      onDragOver={(e) => onDragOver(e, stage.id)}
      onDrop={(e) => onDrop(e, stage.id)}
      onDragLeave={onDragLeave}
      data-testid={`stage-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={cn("w-2 h-2 rounded-full", colors.dot)} />
          <h3 className={cn("font-medium text-sm", colors.text)}>{stage.name}</h3>
          <span className="text-xs text-[#8C8078] bg-white/60 px-1.5 py-0.5 rounded">
            {stageLeads.length}
          </span>
        </div>
      </div>
      
      {/* Stage Stats */}
      <div className="flex items-center justify-between mb-3 text-xs text-[#5D524D]">
        {totalValue > 0 && <span>{formatCurrency(totalValue)}</span>}
        {avgDays > 0 && !['won', 'lost'].includes(stage.id) && (
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Avg {avgDays}d
          </span>
        )}
      </div>
      
      {/* Cards */}
      <div className="space-y-2 min-h-[100px]">
        {stageLeads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onLeadClick}
            isDragging={draggedLead?.id === lead.id}
            selected={selectedLeads?.has(lead.id)}
            onSelect={onToggleSelect}
            showSelect={bulkMode}
          />
        ))}
        
        {stageLeads.length === 0 && (
          <div className="text-center py-8 text-[#8C8078] text-sm">
            No leads
          </div>
        )}
      </div>
    </div>
  );
}

// Lead Detail Modal with Activity Timeline
function LeadDetailModal({ isOpen, onClose, lead, onSave, onAddNote, sources, onConvert, onDelete }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingNote, setAddingNote] = useState(false);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingCRM, setLoadingCRM] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        contactName: lead.contactName || '',
        contactEmail: lead.contactEmail || '',
        contactPhone: lead.contactPhone || '',
        company: lead.company || '',
        source: lead.source || 'Referral',
        estimatedValue: lead.estimatedValue || '',
        stage: lead.stage || 'new',
        notes: lead.notes || '',
        nextActionDate: lead.nextActionDate?.split('T')[0] || '',
        nextAction: lead.nextAction || '',
        assignedTo: lead.assignedTo || ''
      });
      setActiveTab('details');
      // Fetch CRM data
      fetchCRMData();
    }
  }, [lead, isOpen]);

  const fetchCRMData = async () => {
    if (!lead || !token) return;
    setLoadingCRM(true);
    try {
      const [activitiesRes, tasksRes] = await Promise.all([
        axios.get(`${API_BASE}/api/leads/activities/lead/${lead.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE}/api/leads/tasks?lead_id=${lead.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setActivities(activitiesRes.data?.activities || []);
      setTasks(tasksRes.data?.tasks || []);
    } catch (error) {
      console.error('Failed to fetch CRM data:', error);
    } finally {
      setLoadingCRM(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Lead name is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null
      });
    } catch (error) {
      toast.error('Failed to save lead');
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      await onAddNote(lead.id, newNote);
      setNewNote('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
    setAddingNote(false);
  };

  const daysInStage = lead ? getDaysInStage(lead) : 0;

  if (!lead) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">{lead.name}</DialogTitle>
              <p className="text-sm text-[#5D524D] mt-1">
                {lead.company && `${lead.company} • `}
                Created {new Date(lead.createdAt).toLocaleDateString('en-AU')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Stage Badge */}
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-medium",
                STAGE_COLORS[lead.stage]?.bg,
                STAGE_COLORS[lead.stage]?.text
              )}>
                {lead.stage.charAt(0).toUpperCase() + lead.stage.slice(1)}
              </span>
              {/* Days in stage */}
              {!['won', 'lost'].includes(lead.stage) && (
                <span className={cn(
                  "px-2 py-1 rounded-full text-xs flex items-center gap-1",
                  daysInStage > 7 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
                )}>
                  <Clock className="w-3 h-3" />
                  {daysInStage}d in stage
                </span>
              )}
            </div>
          </div>
        </DialogHeader>
        
        {/* Tabs */}
        <div className="flex border-b border-[#E6E0D9] mt-2 overflow-x-auto">
          {[
            { id: 'details', label: 'Details', icon: User },
            { id: 'tasks', label: 'Tasks', icon: ListTodo, count: tasks.filter(t => t.status !== 'completed').length },
            { id: 'timeline', label: 'Timeline', icon: History },
            { id: 'activity', label: 'Activity', icon: Activity, count: activities.length },
            { id: 'qualification', label: 'Qualification', icon: Shield },
            { id: 'ai', label: 'AI Score', icon: Sparkles },
            { id: 'notes', label: 'Notes', icon: MessageSquare, count: lead.notes_history?.length || 0 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id 
                  ? "border-[#4A3B32] text-[#2C2420]" 
                  : "border-transparent text-[#8C8078] hover:text-[#5D524D]"
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="bg-[#4A3B32]/10 text-[#4A3B32] text-xs px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto py-4">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Lead Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Contact Name</label>
                  <Input
                    value={formData.contactName}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Company</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Phone</label>
                  <Input
                    value={formData.contactPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Source</label>
                  <Select value={formData.source} onValueChange={(v) => setFormData(prev => ({ ...prev, source: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Estimated Value</label>
                  <Input
                    type="number"
                    value={formData.estimatedValue}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedValue: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Next Action Date</label>
                  <Input
                    type="date"
                    value={formData.nextActionDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, nextActionDate: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Next Action</label>
                  <Input
                    value={formData.nextAction}
                    onChange={(e) => setFormData(prev => ({ ...prev, nextAction: e.target.value }))}
                    placeholder="e.g., Follow up call"
                  />
                </div>

                <div className="col-span-2">
                  <label className="text-sm font-medium text-[#2C2420] mb-1 block">Assigned To</label>
                  <AdviserPicker
                    value={formData.assignedTo || ''}
                    onChange={(v) => setFormData(prev => ({ ...prev, assignedTo: v.trim() }))}
                    placeholder="Choose internal adviser"
                    testId="lead-edit-assignedto"
                  />
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t border-[#E6E0D9]">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => onDelete(lead.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
                <div className="flex gap-2">
                  {lead.stage === 'won' && !lead.convertedToClient && (
                    <Button type="button" variant="outline" onClick={() => onConvert(lead)}>
                      <UserPlus className="w-4 h-4 mr-1" />
                      Convert to Client
                    </Button>
                  )}
                  <Button type="submit" disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26]">
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </form>
          )}
          
          {/* AI Score Tab */}
          {activeTab === 'ai' && (
            <div className="space-y-4">
              <AILeadScoreComponent lead={lead} />
            </div>
          )}
          
          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <TasksList
              leadId={lead.id}
              tasks={tasks}
              onRefresh={fetchCRMData}
            />
          )}
          
          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <LeadActivityTimeline leadId={lead.id} />
          )}
          
          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <ActivityTimeline
              leadId={lead.id}
              activities={activities}
              onRefresh={fetchCRMData}
            />
          )}
          
          {/* Qualification Tab */}
          {activeTab === 'qualification' && (
            <LeadCompliancePanel
              leadId={lead.id}
              onUpdate={fetchCRMData}
            />
          )}
          
          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="flex gap-2">
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  className="flex-1 min-h-[80px]"
                />
                <Button 
                  onClick={handleAddNote} 
                  disabled={addingNote || !newNote.trim()}
                  className="bg-[#4A3B32] hover:bg-[#3A2E26] self-end"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Notes List */}
              <div className="space-y-3">
                {lead.notes_history?.length > 0 ? (
                  lead.notes_history.map((note, idx) => (
                    <div key={note.id || idx} className="bg-[#F9F7F5] rounded-lg p-3">
                      <p className="text-sm text-[#2C2420]">{note.content}</p>
                      <p className="text-xs text-[#8C8078] mt-2">
                        {note.createdBy} • {new Date(note.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-[#8C8078] text-center py-8">No notes yet</p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add/Create Lead Modal (simplified)
function AddLeadModal({ isOpen, onClose, onSave, sources }) {
  const [formData, setFormData] = useState({
    name: '', contactName: '', contactEmail: '', contactPhone: '',
    company: '', source: 'Referral', estimatedValue: '', stage: 'new',
    notes: '', nextActionDate: '', nextAction: '', assignedTo: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '', contactName: '', contactEmail: '', contactPhone: '',
        company: '', source: 'Referral', estimatedValue: '', stage: 'new',
        notes: '', nextActionDate: '', nextAction: '', assignedTo: ''
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Lead name is required');
      return;
    }
    
    setSaving(true);
    try {
      await onSave({
        ...formData,
        estimatedValue: formData.estimatedValue ? parseFloat(formData.estimatedValue) : null
      });
      onClose();
    } catch (error) {
      toast.error('Failed to create lead');
    }
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Lead Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Smith Family Trust"
                required
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Contact Name</label>
              <Input
                value={formData.contactName}
                onChange={(e) => setFormData(prev => ({ ...prev, contactName: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Company</label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Email</label>
              <Input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Phone</label>
              <Input
                value={formData.contactPhone}
                onChange={(e) => setFormData(prev => ({ ...prev, contactPhone: e.target.value }))}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Source</label>
              <Select value={formData.source} onValueChange={(v) => setFormData(prev => ({ ...prev, source: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Estimated Value</label>
              <Input
                type="number"
                value={formData.estimatedValue}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedValue: e.target.value }))}
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Assigned To</label>
              <AdviserPicker
                value={formData.assignedTo || ''}
                onChange={(v) => setFormData(prev => ({ ...prev, assignedTo: v.trim() }))}
                placeholder="Choose internal adviser"
                testId="lead-add-assignedto"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26]">
              {saving ? 'Creating...' : 'Add Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Pipeline Funnel Component
function PipelineFunnel({ stages, leads }) {
  const funnelData = stages
    .filter(s => !['won', 'lost'].includes(s.id))
    .map(stage => {
      const stageLeads = leads.filter(l => l.stage === stage.id);
      return {
        name: stage.name,
        value: stageLeads.length,
        amount: stageLeads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
        fill: STAGE_COLORS[stage.id]?.hex || '#6B7280'
      };
    });

  if (funnelData.every(d => d.value === 0)) {
    return (
      <div className="text-center py-8 text-[#8C8078]">
        <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No leads in pipeline yet</p>
      </div>
    );
  }

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={funnelData} layout="vertical">
          <XAxis type="number" hide />
          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
          <Tooltip 
            formatter={(value, name, props) => [
              `${value} leads (${formatCurrency(props.payload.amount)})`,
              props.payload.name
            ]}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {funnelData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// Main Leads Kanban Component
export default function LeadsKanban({ onStartTour }) {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [sources, setSources] = useState(LEAD_SOURCES);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [stats, setStats] = useState(null);
  const [showFunnel, setShowFunnel] = useState(true);
  
  // Bulk actions
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const selectAllLeads = () => {
    const all = filteredLeads.map(l => l.id);
    setSelectedLeads(new Set(all));
  };

  const handleBulkAction = async (action, extra = {}) => {
    if (selectedLeads.size === 0) return;
    try {
      await api.post(token, '/leads/bulk-action', {
        leadIds: [...selectedLeads],
        action,
        ...extra
      });
      toast.success(`${selectedLeads.size} lead${selectedLeads.size > 1 ? 's' : ''} updated`);
      setSelectedLeads(new Set());
      setBulkMode(false);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
  };

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    try {
      const [leadsRes, stagesRes, statsRes] = await Promise.all([
        api.get(token, '/leads'),
        api.get(token, '/leads/stages'),
        api.get(token, '/leads/stats')
      ]);
      
      // Add notes count to leads
      const leadsWithNotes = leadsRes.data.leads?.map(lead => ({
        ...lead,
        notesCount: lead.notes_history?.length || 0
      })) || [];
      
      setLeads(leadsWithNotes);
      setStages(stagesRes.data.stages || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    }
    setLoading(false);
  }, [token]);

  const fetchLeadDetails = async (leadId) => {
    try {
      const response = await api.get(token, `/leads/${leadId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
      return null;
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
    setDragOverStage(null);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.stage === stageId) {
      setDragOverStage(null);
      return;
    }

    try {
      await api.put(token, `/leads/${draggedLead.id}/stage`, { stage: stageId });
      
      setLeads(prev => prev.map(l => 
        l.id === draggedLead.id 
          ? { ...l, stage: stageId, stageChangedAt: new Date().toISOString() } 
          : l
      ));
      
      const stageName = stages.find(s => s.id === stageId)?.name || stageId;
      toast.success(`Lead moved to ${stageName}`);
    } catch (error) {
      toast.error('Failed to move lead');
      fetchLeads();
    }
    
    setDraggedLead(null);
    setDragOverStage(null);
  };

  const handleLeadClick = async (lead) => {
    const details = await fetchLeadDetails(lead.id);
    if (details) {
      setSelectedLead(details);
    } else {
      setSelectedLead(lead);
    }
  };

  const handleSaveLead = async (data) => {
    if (selectedLead) {
      await api.put(token, `/leads/${selectedLead.id}`, data);
      toast.success('Lead updated');
      setSelectedLead(null);
    }
    fetchLeads();
  };

  const handleCreateLead = async (data) => {
    await api.post(token, '/leads', data);
    toast.success('Lead created');
    fetchLeads();
  };

  const handleAddNote = async (leadId, content) => {
    await api.post(token, `/leads/${leadId}/notes`, { content });
    // Refresh lead details
    const details = await fetchLeadDetails(leadId);
    if (details) {
      setSelectedLead(details);
      // Update leads list with new notes count
      setLeads(prev => prev.map(l => 
        l.id === leadId ? { ...l, notesCount: (l.notesCount || 0) + 1 } : l
      ));
    }
  };

  const handleConvertToClient = async (lead) => {
    try {
      await api.post(token, `/leads/${lead.id}/convert`);
      toast.success('Lead converted to client!');
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      await api.delete(token, `/leads/${leadId}`);
      toast.success('Lead deleted');
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // Filter leads by search
  const filteredLeads = leads.filter(lead => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(query) ||
      lead.contactName?.toLowerCase().includes(query) ||
      lead.company?.toLowerCase().includes(query) ||
      lead.contactEmail?.toLowerCase().includes(query)
    );
  });

  // Split stages
  const activeStages = stages.filter(s => !['won', 'lost'].includes(s.id));
  const closedStages = stages.filter(s => ['won', 'lost'].includes(s.id));

  // Count stale leads (>7 days in stage)
  const staleLeads = filteredLeads.filter(l => 
    !['won', 'lost'].includes(l.stage) && getDaysInStage(l) > 7
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5D524D]">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full" data-testid="leads-kanban">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-testid="leads-header">
        <div>
          <h2 className="text-xl font-heading text-[#2C2420]">Lead Pipeline</h2>
          <p className="text-sm text-[#5D524D]">
            {stats?.activeLeads || 0} active leads • {formatCurrency(stats?.pipelineValue || 0)} pipeline value
            {staleLeads > 0 && (
              <span className="text-red-600 ml-2">
                • {staleLeads} need attention
              </span>
            )}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFunnel(!showFunnel)}
            className="text-xs"
            data-testid="funnel-toggle-btn"
          >
            <TrendingUp className="w-4 h-4 mr-1" />
            {showFunnel ? 'Hide' : 'Show'} Funnel
          </Button>

          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            onClick={() => { setBulkMode(!bulkMode); setSelectedLeads(new Set()); }}
            className={`text-xs ${bulkMode ? 'bg-[#4A3B32] text-white' : ''}`}
            data-testid="bulk-mode-btn"
          >
            <CheckSquare className="w-4 h-4 mr-1" />
            {bulkMode ? 'Cancel' : 'Select'}
          </Button>
          
          <div className="relative" data-testid="leads-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads..."
              className="pl-9 w-64"
            />
          </div>
          
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-[#4A3B32] hover:bg-[#3A2E26]"
            data-testid="add-lead-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Lead
          </Button>

          {onStartTour && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onStartTour}
              className="text-[#8C8078] hover:text-[#4A3B32] hover:bg-[#4A3B32]/10"
              title="Take a tour of the Leads Pipeline"
              data-testid="leads-tour-btn"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {bulkMode && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-[#FAF5F0] border border-[#D4A574]/30" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium text-[#4A3B32]">
            {selectedLeads.size} selected
          </span>
          <Button variant="ghost" size="sm" onClick={selectAllLeads} className="text-xs h-7" data-testid="bulk-select-all">
            Select All ({filteredLeads.length})
          </Button>
          <div className="flex-1" />
          <Select onValueChange={(stage) => handleBulkAction('change_stage', { stage })}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="bulk-change-stage">
              <SelectValue placeholder="Move to stage..." />
            </SelectTrigger>
            <SelectContent>
              {stages.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm"
            onClick={() => handleBulkAction('archive')}
            disabled={selectedLeads.size === 0}
            className="text-xs h-8"
            data-testid="bulk-archive-btn"
          >
            Archive
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => handleBulkAction('delete')}
            disabled={selectedLeads.size === 0}
            className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            Delete
          </Button>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="leads-quick-stats">
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-4" data-testid="stat-active-leads">
          <p className="text-xs text-[#8C8078] mb-1">Active Leads</p>
          <p className="text-2xl font-heading text-[#2C2420]">{stats?.activeLeads || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-4" data-testid="stat-pipeline-value">
          <p className="text-xs text-[#8C8078] mb-1">Pipeline Value</p>
          <p className="text-2xl font-heading text-[#2C2420]">{formatCurrency(stats?.pipelineValue || 0)}</p>
        </div>
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-4" data-testid="stat-won-month">
          <p className="text-xs text-[#8C8078] mb-1">Won This Month</p>
          <p className="text-2xl font-heading text-green-600">{stats?.wonThisMonth || 0}</p>
          <p className="text-xs text-[#8C8078]">{formatCurrency(stats?.wonValueThisMonth || 0)}</p>
        </div>
        <div className="bg-white rounded-lg border border-[#E6E0D9] p-4" data-testid="stat-conversion-rate">
          <p className="text-xs text-[#8C8078] mb-1">Conversion Rate</p>
          <p className="text-2xl font-heading text-[#2C2420]">{stats?.conversionRate || 0}%</p>
        </div>
      </div>

      {/* Pipeline Funnel */}
      {showFunnel && (
        <div className="bg-white rounded-xl border border-[#E6E0D9] p-4 mb-6">
          <h3 className="text-sm font-medium text-[#2C2420] mb-3">Pipeline Funnel</h3>
          <PipelineFunnel stages={stages} leads={filteredLeads} />
        </div>
      )}

      {/* Stale Leads Alert */}
      {staleLeads > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            <strong>{staleLeads} lead{staleLeads > 1 ? 's' : ''}</strong> ha{staleLeads > 1 ? 've' : 's'} been in the same stage for more than 7 days.
          </p>
        </div>
      )}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {activeStages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            leads={filteredLeads}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            isDragOver={dragOverStage === stage.id}
            onLeadClick={handleLeadClick}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            draggedLead={draggedLead}
            bulkMode={bulkMode}
            selectedLeads={selectedLeads}
            onToggleSelect={toggleLeadSelection}
          />
        ))}
        
        {/* Won/Lost columns */}
        {closedStages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            leads={filteredLeads}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            isDragOver={dragOverStage === stage.id}
            onLeadClick={handleLeadClick}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            draggedLead={draggedLead}
            bulkMode={bulkMode}
            selectedLeads={selectedLeads}
            onToggleSelect={toggleLeadSelection}
          />
        ))}
      </div>

      {/* Add Lead Modal */}
      <AddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleCreateLead}
        sources={sources}
      />

      {/* Lead Detail Modal */}
      <LeadDetailModal
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        lead={selectedLead}
        onSave={handleSaveLead}
        onAddNote={handleAddNote}
        sources={sources}
        onConvert={handleConvertToClient}
        onDelete={handleDeleteLead}
      />
    </div>
  );
}
